/*  ========================================================================
    # Sections - Posts - Pagination
    ========================================================================  */

import React from 'react';
import clsx from 'clsx';
import useSVGSprite from '@/react/hooks/useSVGSprite';

const Pagination = ({ entriesCount, entriesPerPage, currentPage, handlePagination }: {
  entriesCount: number;
  entriesPerPage: number;
  currentPage: number;
  handlePagination: (page: number) => void;
}) => {
  const maxPages = Math.ceil(entriesCount / entriesPerPage);
  const svgSprite = useSVGSprite();

  /**
   * Types.
   */
  const typeNumber = 1;
  const typeSpacer = 2;

  /**
   * Number.
   */
  const paginationNumber = (page: number) => [
    typeNumber,
    <button
      key={page}
      className={clsx('c-pagination__page-number', page === currentPage ? 'c-pagination__page-number--current' : '')}
      onClick={() => handlePagination(page)}
    >
      {page}
    </button>
  ];

  /**
   * Spacer.
   */
  const paginationSpacer = (page: number) => [
    typeSpacer,
    <div key={page} className='c-pagination__spacer'>
      ...
    </div>
  ];

  /**
   * Add number button if it's the first/last page or if the current page is adjacent; otherwise, add spacer.
   */
  const tempPaginationElements = [];
  for (let i = 1; i <= maxPages; i++) {
    tempPaginationElements.push(
      Math.abs(currentPage - i) <= 1 ? paginationNumber(i) : paginationSpacer(i)
    );
  }

  /**
   * Filter function for numbers and spacers.
   */
  const skipDuplicateElements = (item: any, pos: any, arr: any) =>
    item[0] === typeNumber // Render page number.
    || (item[0] === typeSpacer && (pos === 0 || arr[pos - 1][0] !== typeSpacer)); // Skip spacers that are preceded by other spacers.

  /**
   * Filter out duplicate spacers and prepare elements for rendering.
   */
  const paginationElements = tempPaginationElements
    .filter(skipDuplicateElements)
    .map(elem => elem[1]);

  return (
    <div className={clsx('c-pagination', entriesCount <= entriesPerPage ? 'c-pagination--hidden' : '')}>
      <button
        className={clsx('c-pagination__arrow', currentPage - 1 < 1 ? 'c-pagination__arrow--inactive' : '')}
        onClick={() => { if (currentPage - 1 >= 1) handlePagination(currentPage - 1); }}
      >
        <svg style={{ transform: 'translateX(-1px)' }}>
          <use href={`${svgSprite}#chevron-left`} />
        </svg>
      </button>
      {paginationElements}
      <button
        className={clsx('c-pagination__arrow', currentPage + 1 > maxPages ? 'c-pagination__arrow--inactive' : '')}
        onClick={() => { if (currentPage + 1 <= maxPages) handlePagination(currentPage + 1); }}
      >
        <svg style={{ transform: 'translateX(1px)' }}>
          <use href={`${svgSprite}#chevron-right`} />
        </svg>
      </button>
    </div>
  );
};

export default Pagination;
